import {
  categoryNoDataAreas,
  chartLegendEntry,
  escapeHtml,
  NO_DATA_LABEL,
  positionChartTooltip,
  prefersReducedMotion,
  trimMissing,
  type MissingDataMode,
} from "@nexus/widget-kit";
import { withAlpha } from "@nexus/widget-sdk";
import type { ChartColors } from "@nexus/widget-sdk";
import type { IpBlockingSeriesData } from "./types";
import type { IpBlockingOption, IpBlockingLabels } from ".";

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };

export interface CohortSeriesOptions {
  showDetected?: boolean;
  showSafe?: boolean;
  showApproved?: boolean;
  showDetectedLabels?: boolean;
  showSafeLabels?: boolean;
  showApprovedLabels?: boolean;
}

export function buildCohortOption(
  data: IpBlockingSeriesData,
  colors: ChartColors,
  labels: IpBlockingLabels,
  formatDay: (day: string, long?: boolean) => string,
  options: CohortSeriesOptions = {},
  missingData: MissingDataMode = "gap",
): IpBlockingOption {
  const showDetected = options.showDetected ?? true;
  const showSafe = options.showSafe ?? true;
  const showApproved = options.showApproved ?? false;
  const showDetectedLabels = options.showDetectedLabels ?? false;
  const showSafeLabels = options.showSafeLabels ?? true;
  const showApprovedLabels = options.showApprovedLabels ?? false;
  const detectedName =
    data.dateBasis === "blocking_window"
      ? data.targetKind === "DOMAIN"
        ? "Distinct domains"
        : "Distinct IPs"
      : "Detected";
  const approvedName = "Regulator approved";
  const byDay = new Map(data.points.map((point) => [point.day, point]));
  // A day the source could not measure is null, never zero.
  const isMissing = (day: string) => byDay.get(day)?.detected == null;
  const lastWeek = [...new Set(data.points.map((point) => point.day))]
    .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day))
    .sort()
    .slice(-7);
  const days = missingData === "fit" ? trimMissing(lastWeek, isMissing) : lastWeek;
  const missing = days.map(isMissing);
  // Shaded on the first shown series, so hiding the bars keeps it.
  const noDataArea = { silent: true, z: 0, data: categoryNoDataAreas(days, missing, colors) };
  const targetLabel = data.targetKind === "DOMAIN" ? "Domain blocking" : "IP blocking";
  const detectedColor = colors.operational.detection;
  const safeColor =
    colors.classification?.safeSeries ?? colors.classification?.safe ?? colors.series[1];
  // The response tone is the brand purple.
  const approvedColor = colors.operational.response;
  const labelSize = Math.max(12, colors.typography?.labelSize ?? 12);
  const lastIndex = missing.lastIndexOf(false);
  const numberFormat = new Intl.NumberFormat("en");
  const largestCount = Math.max(
    0,
    ...data.points.flatMap((point) => [
      showDetected ? (point.detected ?? 0) : 0,
      showSafe ? (point.safeToBlock ?? 0) : 0,
      showApproved ? (point.approved ?? 0) : 0,
    ]),
  );
  const axisLabelWidth = numberFormat.format(largestCount).length * labelSize * 0.65;
  const shareFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });
  const count = (value: number | null | undefined) =>
    value == null ? "Unavailable" : numberFormat.format(value);
  const row = (marker: string, name: string, value: string) =>
    `<div style="display:flex;justify-content:space-between;gap:16px"><span>${marker}${escapeHtml(name)}</span><b>${escapeHtml(value)}</b></div>`;
  const dot = (color: string) =>
    `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px;background:${color}"></span>`;
  const valueLabel = (show: boolean) => ({
    show,
    position: "top" as const,
    distance: 8,
    formatter: (params: { value?: unknown }) =>
      typeof params.value === "number" ? numberFormat.format(params.value) : "",
    color: colors.tooltipText,
    backgroundColor: colors.tooltipBg,
    borderRadius: 4,
    padding: [3, 6],
    fontSize: labelSize,
    fontWeight: 700,
    ...fontBase,
  });
  type LabelledKey = "detected" | "safeToBlock" | "approved";
  const labelled: LabelledKey[] = [
    ...(showDetected && showDetectedLabels ? ["detected" as const] : []),
    ...(showSafe && showSafeLabels ? ["safeToBlock" as const] : []),
    ...(showApproved && showApprovedLabels ? ["approved" as const] : []),
  ];
  // Labels closer than this share of the scale overlap: the lower one flips under its mark.
  const labelGap = largestCount * 0.15;
  const flipped = new Set<string>();
  days.forEach((day, index) => {
    const point = byDay.get(day);
    const values = labelled
      .flatMap((key) => {
        const value = point?.[key];
        return typeof value === "number" ? [{ key, value }] : [];
      })
      .sort((a, b) => b.value - a.value);
    let floor = Infinity;
    for (const { key, value } of values) {
      const flip = floor - value < labelGap;
      if (flip) flipped.add(`${key}:${index}`);
      floor = flip ? value - labelGap : value;
    }
  });
  const seriesData = (key: LabelledKey, below: "bottom" | "insideTop") =>
    days.map((day, index) => {
      const value = byDay.get(day)?.[key] ?? null;
      return flipped.has(`${key}:${index}`) ? { value, label: { position: below } } : value;
    });
  return {
    animation: !prefersReducedMotion(),
    animationDuration: 900,
    animationEasing: "cubicOut",
    animationDurationUpdate: 500,
    backgroundColor: "transparent",
    title:
      data.points.some((point) => point.detected != null) || days.length > 0
        ? []
        : {
            text: "Data unavailable",
            left: "center",
            top: "middle",
            textStyle: { color: colors.label, fontSize: labelSize, fontWeight: 600, ...fontBase },
          },
    grid: {
      left: labelSize + 24,
      right: 24,
      top: 48,
      bottom: 8,
      containLabel: true,
      show: true,
      // The same quiet plot tint as the piracy audience chart they sit beside.
      backgroundColor: colors.audience?.plot ?? withAlpha(colors.grid, 0.6),
      borderWidth: 0,
    },
    legend: {
      top: 8,
      right: 24,
      type: "scroll",
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 16,
      textStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
      data: [
        // A per-bar color callback leaves the legend on the palette default: set its swatch explicitly.
        ...(showDetected
          ? [
              {
                ...chartLegendEntry(detectedName, "rect"),
                itemStyle: { color: withAlpha(detectedColor, 0.45) },
              },
            ]
          : []),
        // A 4px stroke so each line's swatch reads as clearly as the bar swatch.
        ...(showSafe
          ? [{ name: "Safe to block", icon: "path://M0 0L0 0M16 16L16 16M0 6H16V10H0Z" }]
          : []),
        ...(showApproved
          ? [{ name: approvedName, icon: "path://M0 0L0 0M16 16L16 16M0 6H16V10H0Z" }]
          : []),
      ],
    },
    xAxis: {
      type: "category",
      data: days,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: colors.tooltipText,
        hideOverlap: true,
        margin: 10,
        fontSize: labelSize,
        ...fontBase,
        formatter: (day: string) => formatDay(day),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      splitNumber: 3,
      name: labels.yAxis,
      nameLocation: "middle",
      nameRotate: 90,
      nameGap: axisLabelWidth + 20,
      nameTextStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: colors.tooltipText,
        fontSize: labelSize,
        ...fontBase,
        formatter: (value: number) => numberFormat.format(value),
      },
      splitLine: { lineStyle: { color: colors.grid } },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
      axisPointer: { type: "shadow", shadowStyle: { color: withAlpha(colors.label, 0.08) } },
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      formatter: (params: unknown) => {
        const entries = (Array.isArray(params) ? params : [params]) as { axisValue?: string }[];
        const day = entries[0]?.axisValue;
        if (!day) return "";
        const point = byDay.get(day);
        const heading = `<div style="margin-bottom:4px;opacity:0.75">${escapeHtml(formatDay(day, true))} · ${escapeHtml(targetLabel)}</div>`;
        if (isMissing(day)) return `${heading}<div>${NO_DATA_LABEL}</div>`;
        const share =
          showSafe && point?.detected && point.safeToBlock != null
            ? shareFormat.format(point.safeToBlock / point.detected)
            : null;
        const rows = [
          showDetected ? row(dot(detectedColor), detectedName, count(point?.detected)) : "",
          showSafe ? row(dot(safeColor), "Safe to block", count(point?.safeToBlock)) : "",
          showApproved ? row(dot(approvedColor), approvedName, count(point?.approved)) : "",
        ].join("");
        return `${heading}${rows}${share ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.label, 0.25)}">${row("", "Safe ratio", share)}</div>` : ""}`;
      },
    },
    series: [
      ...(showDetected
        ? [
            {
              name: detectedName,
              type: "bar" as const,
              data: seriesData("detected", "insideTop"),
              barMaxWidth: 56,
              barCategoryGap: "28%",
              // The latest day stands out from the quiet mass of the week before it.
              itemStyle: {
                borderRadius: [4, 4, 0, 0],
                color: (params: { dataIndex: number }) =>
                  withAlpha(detectedColor, params.dataIndex === lastIndex ? 0.6 : 0.3),
              },
              emphasis: { itemStyle: { color: withAlpha(detectedColor, 0.7) } },
              label: valueLabel(showDetectedLabels),
              markArea: noDataArea,
            },
          ]
        : []),
      ...(showSafe
        ? [
            {
              name: "Safe to block",
              type: "line" as const,
              data: seriesData("safeToBlock", "bottom"),
              connectNulls: false,
              symbol: "circle",
              symbolSize: (_value: unknown, params: { dataIndex: number }) =>
                params.dataIndex === lastIndex ? 12 : 8,
              // A ring in the card color keeps each marker readable where it crosses a bar.
              itemStyle: { color: safeColor, borderColor: colors.tooltipBg, borderWidth: 2.5 },
              lineStyle: {
                width: 3.5,
                color: safeColor,
                cap: "round" as const,
                join: "round" as const,
              },
              label: valueLabel(showSafeLabels),
              emphasis: { scale: 1.3 },
              ...(showDetected ? {} : { markArea: noDataArea }),
              z: 3,
            },
          ]
        : []),
      ...(showApproved
        ? [
            {
              name: approvedName,
              type: "line" as const,
              data: seriesData("approved", "bottom"),
              connectNulls: false,
              symbol: "circle",
              symbolSize: (_value: unknown, params: { dataIndex: number }) =>
                params.dataIndex === lastIndex ? 12 : 8,
              itemStyle: { color: approvedColor, borderColor: colors.tooltipBg, borderWidth: 2.5 },
              lineStyle: {
                width: 3.5,
                color: approvedColor,
                cap: "round" as const,
                join: "round" as const,
              },
              label: valueLabel(showApprovedLabels),
              emphasis: { scale: 1.3 },
              ...(showDetected || showSafe ? {} : { markArea: noDataArea }),
              z: 3,
            },
          ]
        : []),
    ],
  };
}
